import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Select from "react-select";
import { Modal, Row, Col, Form } from 'react-bootstrap';
import { NumericFormat as NumberFormat } from "react-number-format";

import { deepEqual } from "../../../../../utils/object";
import { get as getMatters } from "../../../../../actions/matters";
import { get as getStaff } from "../../../../../actions/staff";
import { get as getExpense, save } from "../../../../../actions/expenses";
import { list as listExpenseCodes } from "../../../../../actions/expense.codes";
import { get as getSetting } from "../../../../../actions/settings";
import { render as renderReport } from "../../../../../actions/report.template";
import { search as searchVendors } from "../../../../../actions/vendors";

import DatePickerEx from "../../../../../layout/date-picker";
import ConditionalButton from "../../../../../layout/conditional-button";
import ConditionalDropdown from "../../../../../layout/conditional-dropdown";
import { isGuid } from "../../../../../utils/string";
import { downloadCheckPdf } from "../../../../../utils/checks";
import { format as formatDate } from "../../../../../utils/date";
import { EDIT_CHECK, EDIT_EXPENSE } from "../../../../../constants/permissions";

import Detail from "./detail";

const safeParseJson = (value, fallback = {}) => {
    try {
        return typeof value === "string" ? JSON.parse(value) : (value || fallback);
    } catch (_err) {
        return fallback;
    }
};

class Expense extends React.Component {
    /**
     *
     */
    constructor(props) {
        super(props);
        this.state = {
            data: this.mapPropsToState(props),
            lines: [],
            error: null
        };
    }

    toDate = (v) => (v instanceof Date ? v : v != null ? new Date(v) : null);

    mapPropsToState = (props) => {
        const {
            uuid, caseUuid, staffUuid, personUuid,
            expended, gross, writeOff, net, notes, merchant, show,
            checkNumber, checkDate, vendorUuid
        } = props;

        const now = new Date();
        const s = this.toDate(expended) ?? now;

        return {
            uuid,
            personUuid,
            caseUuid,
            staffUuid,
            expended: s,
            gross,
            writeOff,
            net,
            notes,
            merchant,
            vendorUuid,
            checkNumber: checkNumber || "",
            checkDate: this.toDate(checkDate) ?? s,
            show
        };
    };

    
    componentDidMount = async (e) => {
        await this.loadData();
    }

    componentDidUpdate = (oldProps) => {
        const oldSlice = this.mapPropsToState(oldProps);
        const newSlice = this.mapPropsToState(this.props);
        if (deepEqual(oldSlice, newSlice)) return;
        
        this.setState({data: newSlice}, this.loadData);
    }

    loadData = async () => {
        if (!this.props.show) return;

        const { uuid, staffUuid, caseUuid, personUuid } = this.state.data;
        let base = {}; 
        if (isGuid(uuid)) {
            const expenseData = await this.props.getExpense({ uuid });
            base = {...expenseData}
        }

        const matters = await this.props.getMatters({ staffUuid, personUuid });
        const staff = await this.props.getStaff({ uuid: staffUuid, personUuid, caseUuid });
        const [expenseCodes, vendors, accountsSetting] = await Promise.all([
            this.props.listExpenseCodes(),
            this.props.searchVendors({ page: 0, size: 500, search: "" }),
            this.props.getSetting({ authentic: true, name: "ACCOUNTS" })
        ]);

        this.setState( prev => ({
            matters: (matters?.items ?? []).map((r) => ({
                ...r,
                value: r.uuid,
                label: r.caption
            })),
            staff: (staff ?? []).map((r) => ({
                ...r,
                value: r.uuid,
                label: `${r.Person.displayName}`
            })),
            expenseCodes: (expenseCodes ?? []).map((r) => ({
                ...r,
                value: r.code,
                label: `${r.caption}`,
                onClick: () => {
                    this.setState(prev => ({
                        lines: [...(prev.lines || []), {
                            ...r
                        }]
                    }), () => this.onLineChange(r, this.state.lines.length-1));
                }
            })),
            vendors: (vendors?.items ?? vendors ?? []).map((r) => ({
                ...r,
                value: r.uuid,
                label: r.Person?.displayName || r.displayName || r.uuid
            })),
            lines: [],
            data: {
                ...prev.data,
                ...base,
                checkNumber: prev.data?.checkNumber || "",
                checkDate: prev.data?.checkDate || prev.data?.expended || new Date()
            },
            accountsSetting: safeParseJson(accountsSetting?.ACCOUNTS || accountsSetting?.value || accountsSetting, {})
        }));
    };

    doPostChange = (e) => {

    }

    onCancel = async (e) => {
        this.setState({
            lines: [],
            data: this.mapPropsToState({})
        });

        if (this.props.onCancel) this.props.onCancel(e);
    }

    saveExpense = async () => {
        const { show, ...payload } = this.state.data || {};
        return await this.props.save({ ...payload });
    };

    validateExpense = () => {
        if (!(Number(this.state.data?.gross || 0) > 0)) return "Gross amount must be greater than zero.";
        if (!isGuid(this.state.data?.staffUuid)) return "Staff member is required.";
        if (!String(this.state.data?.notes || "").trim() || String(this.state.data?.notes || "").trim().length <= 5) return "Notes must be longer than five characters.";
        if (!isGuid(this.state.data?.vendorUuid)) return "Vendor is required.";
        return null;
    };

    onOk = async () => {
        const validationError = this.validateExpense();
        if (validationError) {
            this.setState({ error: validationError });
            return;
        }

        const saved = await this.saveExpense();
        if (saved?.success === false || !saved) {
            this.setState({ error: saved?.error?.message || "The expense could not be saved." });
            return;
        }

        if (this.props.onOk) this.props.onOk({ ...this.state.data, uuid: saved.uuid || this.state.data?.uuid });
        
        this.setState({
            lines: [],
            data: this.mapPropsToState({}),
            error: null
        });
    }

    get canWriteCheck() {
        return this.canSave
            && this.canWriteChecks
            && Number(this.state.data?.gross || 0) > 0
            && isGuid(this.state.data?.vendorUuid);
    }

    get canEditChecks() {
        return (this.props.auth?.permissions || []).some((permission) => [EDIT_EXPENSE, EDIT_CHECK].includes(permission));
    }

    get canWriteChecks() {
        return (this.props.auth?.permissions || []).includes(EDIT_CHECK);
    }

    onWriteCheck = async () => {
        if (!this.canWriteCheck) return;

        const validationError = this.validateExpense();
        if (validationError) {
            this.setState({ error: validationError });
            return;
        }

        const saved = await this.saveExpense();
        if (saved?.success === false || !saved) {
            this.setState({ error: saved?.error?.message || "The expense could not be saved." });
            return;
        }

        const data = this.state.data || {};
        const check = {
            amount: Number(data.gross || 0),
            checkDate: data.checkDate || data.expended || new Date(),
            checkNumber: data.checkNumber || "",
            payeeName: data.merchant || this.selectedVendorOption?.label || "",
            memo: data.notes || "",
            referenceNumber: data.checkNumber || "",
        };

        try {
            await downloadCheckPdf({
                render: this.props.renderReport,
                templateUuid: this.state.accountsSetting?.general?.check,
                fileName: `${formatDate(check.checkDate)} Check ${check.checkNumber || check.payeeName || "Expense"}.pdf`,
                check,
            });
        } catch (_error) {
            this.setState({ error: "The expense was saved, but the check could not be rendered." });
            return;
        }

        if (this.props.onOk) this.props.onOk({ ...this.state.data, uuid: saved.uuid || this.state.data?.uuid });

        this.setState({
            lines: [],
            data: this.mapPropsToState({}),
            error: null
        });
    };

    onHideModal = (e) => {
        this.onCancel(e);
    }

    onChange = (e) => {
        const { name, value } = e.target;
        if (["checkNumber", "checkDate"].includes(name) && !this.canEditChecks) return;
        if (this.state.data?.[name] === value) return;

        this.setState(prev => ({
            data: { ...prev.data, [name]: value }
        }), this.doPostChange);
    };

    get selectedVendorOption() {
        return this.state.vendors?.find(option => option.value === this.state.data?.vendorUuid) || null;
    }

    onVendorChange = (option) => {
        this.setState(prev => ({
            data: {
                ...prev.data,
                vendorUuid: option?.value || null,
                merchant: option?.label || ""
            }
        }), this.doPostChange);
    };

    onGrossChange = (e) => {
        const gross = Number(e.target.value ?? 0);
        this.setState(prev => {
            const writeOff = Number(prev.data?.writeOff ?? 0);
            return {
                data: {
                    ...prev.data,
                    gross,
                    net: gross - writeOff,
                }
            };
        });
    };

    onWriteOffChange = (e) => {
        const writeOff = Number(e.target.value ?? 0);
        this.setState(prev => {
            const gross = Number(prev.data?.gross ?? 0);
            return {
                data: {
                    ...prev.data,
                    writeOff,
                    net: gross - writeOff,
                }
            };
        });
    };

    onNetChange = (e) => {
        const net = Number(e.target.value ?? 0);
        this.setState(prev => {
            const gross = Number(prev.data?.gross ?? 0);
            const safeNet = Math.min(gross, net);
            return {
                data: {
                    ...prev.data,
                    net: safeNet,
                    writeOff: gross - safeNet,
                }
            };
        });
    };

    doCalcNotes = () => {
        this.setState(prev => {
            const lines = prev.lines || [];

            const gross = lines.reduce((sum, l) => sum + (Number(l.extended) || 0), 0);

            const notes = lines
                .map(l => l.note)
                .filter(Boolean)
                .join(" | ");

            return {
                data: { ...prev.data, gross, notes }
            };
        });
    };

    onLineChange = (e, index) => {
        const quantity = Number(e.quantity ?? 0);
        const each = Number(e.each ?? 0);

        const patch = {
            ...e,
            extended: quantity * each,
            note: `${e.caption ?? ""} (${quantity} @ $${each.toFixed(2)})`,
        };



        this.setState(prev => ({
            lines: (prev.lines || []).map((item, i) =>
                i === index ? { ...item, ...patch } : item
            )
        }), this.doCalcNotes);
    };

    get canSave() {
        return ((this.state.data?.gross > 0) && (isGuid(this.state.data?.staffUuid)) && (this.state.data?.notes?.length > 5) && isGuid(this.state.data?.vendorUuid));
    }

    render() {
        return (
            <>
                <Modal size="lg" backdrop="static" keyboard show={this.props.show} onHide={this.onHideModal}>
                    <Modal.Header closeButton>
                        <Modal.Title id="staticBackdropLabel">{this.props.title || `Expense`}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row className="g-3">
                            {this.state.error ? (
                                <Col xs="12">
                                    <div className="alert alert-danger mb-0">{this.state.error}</div>
                                </Col>
                            ) : null}
                            <Col md="10">
                                <Row className="g-3 pb-3 mt-1">
                                    {!this.props.caseUuid &&
                                        <Col xs="12">
                                            <div className="top-label">
                                                <Select
                                                    id="caseUuid"
                                                    value={this.state.matters?.find(option => option.value === this.state.data?.caseUuid)}
                                                    name="caseUuid"
                                                    onChange={(e) => this.onChange(
                                                        {
                                                            target: {
                                                                option: e,
                                                                name: "caseUuid",
                                                                value: e?.value
                                                            }
                                                        }
                                                    )}
                                                    classNamePrefix="react-select"
                                                    placeholder="Matter"
                                                    options={this.state.matters}
                                                    isClearable
                                                >
                                                </Select>
                                                <Form.Label>MATTER</Form.Label>
                                            </div>
                                        </Col>
                                    }
                                    {!this.props.staffUuid &&
                                        <>
                                            <Col xs="12">
                                                <div className="top-label">
                                                    <Select
                                                        id="staffUuid"
                                                        value={this.state.staff?.find(option => option.value === this.state.data?.staffUuid)}
                                                        name="staffUuid"
                                                        onChange={(e) => this.onChange(
                                                            {
                                                                target: {
                                                                    option: e,
                                                                    name: "staffUuid",
                                                                    value: e.value
                                                                }
                                                            }
                                                        )}
                                                        classNamePrefix="react-select"
                                                        placeholder="Staff Member"
                                                        options={this.state.staff}
                                                    >
                                                    </Select>
                                                    <Form.Label>STAFF MEMBER</Form.Label>
                                                </div>
                                            </Col>
                                        </>
                                    }
                                </Row>
                            </Col>
                            <Col md="2">
                                <Row className="g-3 pb-3 mt-5">
                                    <Col className="text-end">
                                        <ConditionalDropdown className="ms-sm-1" enabledVariant="secondary" enabled display items={this.state.expenseCodes}>Add</ConditionalDropdown>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        {this.state.lines?.map((item, index) => {
                            return (
                                <Detail key={`line_idx_${index}`} {...item} onChange={(e) => { this.onLineChange(e, index); }} />
                            );
                        })}
                        <Row className="g-3">
                            <Col xs="12" md="4">
                                <div className="top-label">
                                    <NumberFormat
                                        displayType="input"
                                        type="text"
                                        id="gross"
                                        name="gross"
                                        className="text-end form-control mb-2"
                                        prefix="$"
                                        thousandSeparator={false}
                                        decimalScale={2}
                                        allowNegative={false}
                                        value={this.state.data?.gross ?? 0}
                                        onValueChange={(e) => this.onGrossChange({ target: { name: "gross", value: e.floatValue } })}
                                        removeFormatting
                                        isAllowed={(values) => {
                                            const { floatValue } = values;
                                            return floatValue == null || floatValue <= 5000000;
                                        }}
                                    />
                                    <Form.Label>GROSS</Form.Label>
                                </div>
                            </Col>
                            <Col xs="12" md="4">
                                <div className="top-label">
                                    <NumberFormat
                                        displayType="input"
                                        type="text"
                                        id="writeOff"
                                        name="writeOff"
                                        className="text-end form-control mb-2"
                                        prefix="$"
                                        thousandSeparator={false}
                                        decimalScale={2}
                                        allowNegative={false}
                                        value={this.state.data?.writeOff ?? 0}
                                        onValueChange={(e) => this.onWriteOffChange({ target: { name: "writeOff", value: e.floatValue } })}
                                        removeFormatting
                                        isAllowed={(values) => {
                                            const { floatValue } = values;
                                            return floatValue == null || floatValue <= this.state.data?.gross;
                                        }}
                                    />
                                    <Form.Label>WRITE OFF</Form.Label>
                                </div>
                            </Col>
                            <Col xs="12" md="4">
                                <div className="top-label">
                                    <NumberFormat
                                        displayType="input"
                                        type="text"
                                        id="net"
                                        name="net"
                                        className="text-end form-control mb-2"
                                        prefix="$"
                                        thousandSeparator={false}
                                        decimalScale={2}
                                        allowNegative={false}
                                        value={this.state.data?.net ?? 0}
                                        onValueChange={(e) => this.onNetChange({ target: { name: "net", value: e.floatValue } })}
                                        removeFormatting
                                        isAllowed={(values) => {
                                            const { floatValue } = values;
                                            return floatValue == null || floatValue <= (this.state.data?.gross - this.state.data?.writeOff);
                                        }}
                                    />
                                    <Form.Label>NET</Form.Label>
                                </div>
                            </Col>
                            <Col xs="12">
                                <div className="top-label">
                                    <Form.Control type="text" name="notes" value={this.state.data?.notes || ""} onChange={this.onChange} />
                                    <Form.Label>NOTES</Form.Label>
                                </div>
                            </Col>
                            <Col xs="12" md="8">
                                <div className="top-label">
                                    <Select
                                        inputId="expenseVendorUuid"
                                        value={this.selectedVendorOption}
                                        name="vendorUuid"
                                        onChange={this.onVendorChange}
                                        classNamePrefix="react-select"
                                        placeholder="Vendor"
                                        options={this.state.vendors || []}
                                        isClearable
                                    />
                                    <Form.Label>VENDOR</Form.Label>
                                </div>
                            </Col>
                            <Col xs="12" md="4">
                                <div className="top-label">
                                    <DatePickerEx
                                        id="expended"
                                        selected={this.state.data?.expended}
                                        name="expended"
                                        onChange={(date) => this.onChange({ target: { name: "expended", value: date } })}
                                        className="form-control"
                                        dateFormat="MM/dd/yyyy h:mm aa"
                                        timeInputLabel="Time:"
                                        showTimeInput
                                    />
                                    <Form.Label>DATE OF</Form.Label>
                                </div>
                            </Col>
                            <Col xs="12" md="4">
                                <div className="top-label">
                                    <Form.Control
                                        type="text"
                                        name="checkNumber"
                                        value={this.state.data?.checkNumber || ""}
                                        disabled={!this.canEditChecks}
                                        onChange={this.onChange}
                                    />
                                    <Form.Label>CHECK NUMBER</Form.Label>
                                </div>
                            </Col>
                            <Col xs="12" md="4">
                                <div className="top-label">
                                    <DatePickerEx
                                        id="checkDate"
                                        selected={this.state.data?.checkDate}
                                        name="checkDate"
                                        disabled={!this.canEditChecks}
                                        onChange={(date) => this.onChange({ target: { name: "checkDate", value: date } })}
                                        className="form-control"
                                        dateFormat="MM/dd/yyyy"
                                    />
                                    <Form.Label>CHECK DATE</Form.Label>
                                </div>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <ConditionalButton enabledVariant="outline-primary" display enabled onEnabledClick={this.onCancel} icon="error-hexagon">cancel</ConditionalButton>
                        <ConditionalButton enabledVariant="outline-primary" display enabled={this.canWriteCheck} onEnabledClick={this.onWriteCheck} icon="print">write check</ConditionalButton>
                        <ConditionalButton enabledVariant="outline-primary" display enabled onEnabledClick={this.onOk} icon="save">ok</ConditionalButton>
                    </Modal.Footer>
                </Modal>
            </>
        )
    }
}

Expense.propTypes = {
    auth: PropTypes.object.isRequired
}

const mapStateToProps = (state) => {
    return ({
        auth: state.auth
    });
};

export default connect(mapStateToProps,
    {
        getMatters,
        getStaff,
        getExpense,
        listExpenseCodes,
        getSetting,
        renderReport,
        searchVendors,
        save
    }
)(Expense);
